import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Rule from '@starbucks-web/pattern-library/lib/components/rule';

import Frap from 'shared/app/components/frap-with-messages';
import FrapContainer from 'shared/app/components/frap-container';
import PurchaseFormSection from './purchase-form-section';
import InputMessage from './input-message';
import GiftAmount from './gift-amount';
import Recipient from './recipient';
import Sender from './sender';
import {
  trackGiftCartView,
  trackGiftCartCheckoutClick,
} from '../../state/track-event';
import { numberOfRecipientsSelector } from '../../state/selectors';

import { routeParamsSelector } from 'shared/app/state/selectors/routes';

import { DEFAULT_SEND_METHOD } from '../../utils/gift-form';
import messages from './messages';
import AddRecipient from './add-recipient';

const PurchaseForm = ({
  fields,
  maxMessageLength,
  recipients,
  setRecipients,
}) => {
  const [customOptionValue, setCustomOptionValue] = useState('custom');
  const [senderNameValue, setSenderNameValue] = useState('');
  const [senderEmailValue, setSenderEmailValue] = useState('');
  const [messageValue, setMessageValue] = useState('');
  const { amount, customAmount, senderName, senderEmail, message } = fields;
  const numberOfRecipients = useSelector(numberOfRecipientsSelector);
  const routeParams = useSelector(routeParamsSelector);
  const recipientsRef = useRef([]);
  useEffect(() => {
    // ref array adapts naturally to recipient changes; removed items convert to `null` and this `slice` cleans them up
    recipientsRef.current = recipientsRef?.current?.slice(0, recipients.length);
  }, [recipients]);

  useEffect(() => {
    // eslint-disable-next-line camelcase
    trackGiftCartView({ gift_card_id: routeParams.productNumber });
  }, []);

  const moveFocusOnClick = (index) => {
    recipientsRef?.current[index]?.focus();
  };

  const totalGiftAmount = () => {
    const giftAmount = amount.input.value;
    const customGiftAmount = customAmount?.input?.value;

    // if custom gift amount is invalid - return 0 so we can hide the gift amount in the frap
    if (giftAmount === 'custom' && customAmount?.error) {
      return 0;
    }
    const amountPerGift =
      giftAmount === 'custom' ? customGiftAmount : giftAmount;
    return amountPerGift * numberOfRecipients;
  };

  return (
    <div className="mt4">
      <PurchaseFormSection
        headerText={messages.giftAmountSectionHeader}
        numberOfRecipients={numberOfRecipients}
      >
        <GiftAmount
          amount={amount}
          customAmount={customAmount}
          customOptionValue={customOptionValue}
          setCustomOptionValue={setCustomOptionValue}
        />
      </PurchaseFormSection>
      <PurchaseFormSection headerText={messages.recipientInfoSectionHeader}>
        <ul>
          {recipients.map((recipient, i) => {
            const { id } = recipient;
            return (
              <li key={id}>
                <Recipient
                  currentIndex={i}
                  currentRecipient={recipient}
                  fields={fields}
                  moveFocusOnClick={moveFocusOnClick}
                  recipientEmail={fields[`recipientEmail${id}`]}
                  recipientName={fields[`recipientName${id}`]}
                  recipients={recipients}
                  ref={(el) => (recipientsRef.current[i] = el)}
                  sendMethod={DEFAULT_SEND_METHOD}
                  setRecipients={setRecipients}
                />
                {i !== recipients.length - 1 ? (
                  <Rule className="my6" weight="thin" />
                ) : null}
              </li>
            );
          })}
        </ul>
      </PurchaseFormSection>
      <PurchaseFormSection>
        <AddRecipient
          moveFocusOnClick={moveFocusOnClick}
          recipients={recipients}
          setRecipients={setRecipients}
        />
      </PurchaseFormSection>

      <PurchaseFormSection
        headerText={messages.messageSectionHeader}
        numberOfRecipients={numberOfRecipients}
      >
        <InputMessage
          maxMessageLength={maxMessageLength}
          message={message}
          messageValue={messageValue}
          setMessageValue={setMessageValue}
        />
      </PurchaseFormSection>
      <PurchaseFormSection headerText={messages.senderInfoSectionHeader}>
        <fieldset>
          <legend className="hiddenVisually">
            <FormattedMessage {...messages.senderInformationLabel} />
          </legend>
          <Sender
            senderEmail={senderEmail}
            senderEmailValue={senderEmailValue}
            senderName={senderName}
            senderNameValue={senderNameValue}
            setSenderEmailValue={setSenderEmailValue}
            setSenderNameValue={setSenderNameValue}
          />
        </fieldset>
      </PurchaseFormSection>
      <FrapContainer>
        <Frap
          data-e2e="giftCheckout"
          onClick={() =>
            trackGiftCartCheckoutClick({
              // eslint-disable-next-line camelcase
              gift_card_id: routeParams.productNumber,
            })
          }
          type="submit"
        >
          <FormattedMessage
            {...messages.checkoutFrap}
            values={{ amount: totalGiftAmount() }}
          />
        </Frap>
      </FrapContainer>
    </div>
  );
};

PurchaseForm.propTypes = {
  fields: PropTypes.object,
  maxMessageLength: PropTypes.number,
  recipients: PropTypes.array,
  setRecipients: PropTypes.func,
};

export default PurchaseForm;
